import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/app/Header";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, UploadSimple, Trash, DownloadSimple, Sparkle, Info, PencilSimple, ArrowsClockwise, Eye, X,
} from "@phosphor-icons/react";
import TemplatePreviewDialog from "@/components/app/TemplatePreviewDialog";

/**
 * Template library page (`/templates`). Solicitor-only.
 *
 * Solicitors can:
 *   - Upload .docx templates (name + optional description + the file itself)
 *   - Download / delete existing templates
 *   - Inspect the available `{{merge_tags}}` cheat sheet so they know what
 *     to paste into their .docx before uploading.
 *
 * Generation lives on each case's Documents tab via
 * `GenerateFromTemplateButton`, not here — this page is purely about
 * managing the library.
 */
export default function DocumentTemplatesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  // Curated registry of dotted case paths a solicitor can map a custom
  // merge tag to. Loaded once from /document-templates/case-fields.
  const [caseFields, setCaseFields] = useState([]);
  // Free-form filter chip — when set, only templates with this label show.
  const [labelFilter, setLabelFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadLabels, setUploadLabels] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  // Edit metadata + Replace file state — both target one existing template
  // identified by `editing.id`; mode flips the dialog content.
  const [editing, setEditing] = useState(null);  // { template, mode: "metadata" | "replace" }
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editLabels, setEditLabels] = useState([]); // string[]
  const [editLabelInput, setEditLabelInput] = useState("");
  const [editCustomTags, setEditCustomTags] = useState([]); // [{tag,path}]
  // Per-template merge-tag display labels. Stored as [{tag,label}]
  // rows so the user can reorder/edit/remove freely without us
  // hard-coding which tags are eligible. Empty labels are dropped on
  // save.
  const [editFieldLabels, setEditFieldLabels] = useState([]); // [{tag,label,kind}]
  // Pack-slot binding — pairs a template to its role in the Onboarding
  // Client Care Pack. Empty string = unbound. The slot catalogue lives
  // on the backend at /api/onboarding-pack/slots so we don't duplicate
  // the list of slot keys here.
  const [editPackSlot, setEditPackSlot] = useState("");
  const [packSlotCatalogue, setPackSlotCatalogue] = useState([]);

  useEffect(() => {
    let cancelled = false;
    API.get("/onboarding-pack/slots").then(({ data }) => {
      if (!cancelled) setPackSlotCatalogue(data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [replaceFile, setReplaceFile] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const replaceFileRef = useRef(null);
  // Preview dialog targets one template at a time.
  const [previewTpl, setPreviewTpl] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role !== "solicitor") {
      toast.error("Solicitor access required");
      navigate("/");
    }
  }, [user, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tplsRes, tagsRes, fieldsRes] = await Promise.all([
        API.get("/document-templates"),
        API.get("/document-templates/merge-tags"),
        API.get("/document-templates/case-fields"),
      ]);
      setTemplates(tplsRes.data);
      setTags(tagsRes.data);
      setCaseFields(fieldsRes.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (user?.role === "solicitor") fetchAll(); }, [user, fetchAll]);

  const handleUpload = async () => {
    if (!uploadFile) { toast.error("Pick a .docx file first"); return; }
    if (!uploadName.trim()) { toast.error("Template name is required"); return; }
    if (!uploadFile.name.toLowerCase().endsWith(".docx")) {
      toast.error("Only .docx files are supported");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const qs = `?name=${encodeURIComponent(uploadName.trim())}&description=${encodeURIComponent(uploadDesc.trim())}&labels=${encodeURIComponent(uploadLabels.trim())}`;
      await API.post(`/document-templates${qs}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Template uploaded");
      setUploadOpen(false);
      setUploadName(""); setUploadDesc(""); setUploadLabels(""); setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (tpl) => {
    try {
      const { data } = await API.get(`/document-templates/${tpl.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = tpl.original_filename || `${tpl.name}.docx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handleRepairMortgage = async (tpl) => {
    if (!window.confirm(`Repair "${tpl.name}"?\n\nThis replaces the template with the corrected version (removes the old signature, sign-off uses the acting solicitor). Documents already generated are not changed.`)) return;
    setRepairing(true);
    try {
      await API.post("/document-templates/repair-mortgage-report");
      toast.success("Mortgage Report template repaired. New generations use the corrected version.");
      await fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Repair failed");
    } finally {
      setRepairing(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/document-templates/${tpl.id}`);
      toast.success("Template deleted");
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  /**
   * Open the Edit-template dialog. `mode` decides which form we show:
   *   - "metadata": rename + edit description (no file touch)
   *   - "replace":  swap the .docx contents (name/description preserved)
   * Both routes hit the same backend record, so we hand back the refreshed
   * row to the caller via fetchAll().
   */
  const openEditDialog = (tpl, mode) => {
    setEditing({ template: tpl, mode });
    setEditName(tpl.name || "");
    setEditDesc(tpl.description || "");
    setEditLabels(Array.isArray(tpl.labels) ? [...tpl.labels] : []);
    setEditLabelInput("");
    // custom_tags arrives as {slug: path} — flatten to an editable list
    // of {tag,path} rows so the user can reorder/edit/remove freely.
    const ct = tpl.custom_tags || {};
    setEditCustomTags(Object.entries(ct).map(([tag, path]) => ({ tag, path })));
    const fl = tpl.field_labels || {};
    const ft = tpl.field_types || {};
    // Union of tags present in either map so neither view drops rows.
    const allTags = Array.from(new Set([...Object.keys(fl), ...Object.keys(ft)]));
    setEditFieldLabels(allTags.map((tag) => ({ tag, label: fl[tag] || "", kind: ft[tag] || "text" })));
    setEditPackSlot(tpl.pack_slot || "");
    setReplaceFile(null);
    if (replaceFileRef.current) replaceFileRef.current.value = "";
  };

  const closeEditDialog = () => {
    setEditing(null);
    setReplaceFile(null);
    setEditName("");
    setEditDesc("");
    setEditLabels([]);
    setEditLabelInput("");
    setEditCustomTags([]);
    setEditFieldLabels([]);
    setEditPackSlot("");
  };

  // Label-chip input helpers — Enter / comma commit a chip.
  const commitLabel = () => {
    const v = editLabelInput.trim();
    if (!v) return;
    if (editLabels.some((l) => l.toLowerCase() === v.toLowerCase())) {
      setEditLabelInput("");
      return;
    }
    setEditLabels([...editLabels, v.slice(0, 40)]);
    setEditLabelInput("");
  };
  const removeLabel = (l) => setEditLabels(editLabels.filter((x) => x !== l));

  const handleSaveMetadata = async () => {
    if (!editName.trim()) { toast.error("Template name is required"); return; }
    setSavingEdit(true);
    try {
      // Flatten custom_tags rows → {slug:path}. Drop blanks; backend
      // validates dotted paths against the whitelist + de-dupes slugs.
      const ct = {};
      for (const { tag, path } of editCustomTags) {
        const slug = (tag || "").trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
        if (!slug || !path) continue;
        ct[slug] = path;
      }
      await API.put(`/document-templates/${editing.template.id}`, {
        name: editName.trim(),
        description: editDesc.trim(),
        labels: editLabels,
        custom_tags: ct,
        // Flatten field-label rows → {slug: label}; drop blanks. Backend
        // strips overlong labels and trims whitespace.
        field_labels: Object.fromEntries(
          editFieldLabels
            .map(({ tag, label }) => [(tag || "").trim(), (label || "").trim()])
            .filter(([t, l]) => t && l),
        ),
        field_types: Object.fromEntries(
          editFieldLabels
            .map(({ tag, kind }) => [(tag || "").trim(), (kind || "text").trim()])
            .filter(([t, k]) => t && k && k !== "text"),
        ),
        pack_slot: editPackSlot || "",
      });
      toast.success("Template updated");
      closeEditDialog();
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleReplaceFile = async () => {
    if (!replaceFile) { toast.error("Pick a .docx file first"); return; }
    if (!replaceFile.name.toLowerCase().endsWith(".docx")) {
      toast.error("Only .docx files are supported");
      return;
    }
    setSavingEdit(true);
    try {
      const fd = new FormData();
      fd.append("file", replaceFile);
      await API.post(`/document-templates/${editing.template.id}/replace-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Template file replaced");
      closeEditDialog();
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Replace failed");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="templates-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Templates
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Upload .docx templates with <code className="font-mono text-violet-700 bg-violet-50 px-1 rounded">{"{{merge_tags}}"}</code>. Generate filled .docx + .pdf from any case&apos;s Documents tab.
            </p>
          </div>
          <Button
            onClick={() => setUploadOpen(true)}
            data-testid="upload-template-btn"
            className="text-white gap-1.5"
            style={{ background: "#100f5d" }}
          >
            <UploadSimple weight="bold" className="w-4 h-4" /> Upload template
          </Button>
        </div>

        {labelFilter && (
          <div className="flex items-center gap-2 mb-4" data-testid="active-label-filter">
            <span className="text-xs text-slate-500">Filtering by label:</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-200">
              {labelFilter}
              <button
                type="button"
                onClick={() => setLabelFilter("")}
                className="text-violet-500 hover:text-violet-700"
                title="Clear filter"
                data-testid="clear-label-filter"
              >
                ×
              </button>
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Templates list */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            {loading ? (
              <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
            ) : templates.length === 0 ? (
              <div className="py-12 text-center text-sm text-slate-400" data-testid="templates-empty">
                <FileText weight="duotone" className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                No templates yet. Click <span className="font-medium">Upload template</span> to add your first one.
              </div>
            ) : (() => {
              // Filter by selected label chip (or none — show everything).
              const visible = labelFilter
                ? templates.filter((t) => Array.isArray(t.labels) && t.labels.includes(labelFilter))
                : templates;
              if (visible.length === 0) {
                return (
                  <div className="py-12 text-center text-sm text-slate-400" data-testid="templates-empty-filter">
                    No templates match label <span className="font-medium">"{labelFilter}"</span>.
                  </div>
                );
              }
              return (
              <ul className="divide-y divide-slate-100" data-testid="templates-list">
                {visible.map((t) => (
                  <li key={t.id} className="p-4 hover:bg-slate-50/50 transition-colors" data-testid={`template-row-${t.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(200,125,58,0.12)" }}>
                          <FileText weight="bold" className="w-4 h-4" style={{ color: "#c87d3a" }} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                          {t.description && (
                            <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>
                          )}
                          {Array.isArray(t.labels) && t.labels.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`template-labels-${t.id}`}>
                              {t.labels.map((l) => (
                                <button
                                  key={l}
                                  type="button"
                                  onClick={() => setLabelFilter(labelFilter === l ? "" : l)}
                                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full transition-colors ${
                                    labelFilter === l
                                      ? "bg-violet-100 text-violet-800 border border-violet-200"
                                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent"
                                  }`}
                                  title={`Filter to "${l}" templates`}
                                  data-testid={`template-label-${t.id}-${l}`}
                                >
                                  {l}
                                </button>
                              ))}
                            </div>
                          )}
                          {t.custom_tags && Object.keys(t.custom_tags).length > 0 && (
                            <p className="text-[10px] text-violet-600 mt-1" title="Custom merge tags configured on this template">
                              {Object.keys(t.custom_tags).length} custom tag{Object.keys(t.custom_tags).length === 1 ? "" : "s"}
                            </p>
                          )}
                          <p className="text-[10px] text-slate-400 mt-1">
                            {t.original_filename} · {(t.size / 1024).toFixed(1)} KB · uploaded by {t.uploaded_by_name} on {new Date(t.created_at).toLocaleDateString("en-GB")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" onClick={() => openEditDialog(t, "metadata")} data-testid={`edit-template-${t.id}`}>
                          <PencilSimple weight="bold" className="w-3.5 h-3.5" /> Edit
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" onClick={() => setPreviewTpl(t)} data-testid={`preview-template-${t.id}`} title="Preview merged output against any case">
                          <Eye weight="bold" className="w-3.5 h-3.5" /> Preview
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" onClick={() => handleDownload(t)} data-testid={`download-template-${t.id}`}>
                          <DownloadSimple weight="bold" className="w-3.5 h-3.5" /> Download
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" onClick={() => openEditDialog(t, "replace")} data-testid={`replace-template-${t.id}`}>
                          <ArrowsClockwise weight="bold" className="w-3.5 h-3.5" /> Replace
                        </Button>
                        {/mortgage.*report/i.test(t.name || "") && (
                          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1 text-[#100f5d]" onClick={() => handleRepairMortgage(t)} disabled={repairing} data-testid={`repair-template-${t.id}`} title="Replace this template with the corrected version (removes the old signature)">
                            <Sparkle weight="bold" className="w-3.5 h-3.5" /> {repairing ? "Repairing…" : "Repair signature"}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1 text-red-600 hover:text-red-700" onClick={() => handleDelete(t)} data-testid={`delete-template-${t.id}`}>
                          <Trash weight="bold" className="w-3.5 h-3.5" /> Delete
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              );
            })()}
          </div>

          {/* Merge tag cheat sheet */}
          <aside className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm h-fit sticky top-20" data-testid="merge-tag-cheatsheet">
            <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              <Sparkle weight="bold" className="w-4 h-4 text-violet-500" /> Merge tags
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Paste any of these into your .docx (as <code className="font-mono bg-slate-100 px-1 rounded">{"{{tag}}"}</code>). Unknown tags are left as-is.
            </p>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {tags.map((g) => (
                <div key={g.group}>
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">{g.group}</p>
                  <div className="flex flex-wrap gap-1">
                    {g.tags.map((tag) => (
                      <code
                        key={tag}
                        onClick={() => {
                          navigator.clipboard?.writeText(`{{${tag}}}`);
                          toast.success(`Copied {{${tag}}}`);
                        }}
                        className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 cursor-pointer hover:bg-violet-100 transition-colors"
                        data-testid={`merge-tag-${tag}`}
                      >
                        {`{{${tag}}}`}
                      </code>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-200 flex items-start gap-2 text-[11px] text-slate-500">
              <Info weight="bold" className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
              <span>Click any tag to copy it.</span>
            </div>
          </aside>
        </div>
      </main>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-md" data-testid="upload-template-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Upload template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Template name</Label>
              <Input
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                placeholder="e.g., Client Care Letter"
                maxLength={120}
                data-testid="upload-template-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Description (optional)</Label>
              <Textarea
                value={uploadDesc}
                onChange={(e) => setUploadDesc(e.target.value)}
                placeholder="When to use this template…"
                rows={2}
                data-testid="upload-template-desc"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Labels (optional)</Label>
              <Input
                value={uploadLabels}
                onChange={(e) => setUploadLabels(e.target.value)}
                placeholder="Sale, Buy-to-Let, Commercial"
                data-testid="upload-template-labels"
              />
              <p className="text-[10px] text-slate-400">
                Comma-separated. Used for filtering in the template library. Edit any time.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">.docx file</Label>
              <Input
                type="file"
                ref={fileInputRef}
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="upload-template-file"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadFile || !uploadName.trim()} className="text-white" style={{ background: "#100f5d" }} data-testid="upload-template-submit">
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit / Replace dialog — shared between metadata-edit and file-swap
          flows so we only have one piece of UI to maintain. The `mode` on
          `editing` picks the inner form. */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeEditDialog(); }}>
        <DialogContent className="sm:max-w-xl" data-testid="edit-template-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
              {editing?.mode === "replace" ? "Replace template file" : "Edit template"}
            </DialogTitle>
          </DialogHeader>
          {editing?.mode === "metadata" ? (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Template name</Label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={120}
                  data-testid="edit-template-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Description</Label>
                <Textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={2}
                  data-testid="edit-template-desc"
                />
              </div>
              <div className="space-y-1.5" data-testid="edit-template-labels">
                <Label className="text-xs font-medium">Labels</Label>
                <div className="flex flex-wrap items-center gap-1 border border-slate-200 rounded-md px-2 py-1.5 min-h-[34px] bg-white">
                  {editLabels.map((l) => (
                    <span key={l} className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-800">
                      {l}
                      <button
                        type="button"
                        onClick={() => removeLabel(l)}
                        className="text-violet-500 hover:text-violet-700"
                        title="Remove"
                        data-testid={`remove-label-${l}`}
                      >×</button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="flex-1 min-w-[80px] outline-none text-[12px] py-0.5"
                    value={editLabelInput}
                    onChange={(e) => setEditLabelInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        commitLabel();
                      } else if (e.key === "Backspace" && !editLabelInput && editLabels.length) {
                        setEditLabels(editLabels.slice(0, -1));
                      }
                    }}
                    onBlur={commitLabel}
                    placeholder={editLabels.length === 0 ? "Add labels (Enter to confirm)" : ""}
                    data-testid="edit-template-label-input"
                  />
                </div>
                <p className="text-[10px] text-slate-400">
                  Used to filter the template library. Click any label in the list to filter by it.
                </p>
              </div>

              {/* Onboarding Pack slot binding — opt-in. Picking a slot
                  marks this template as the firm-wide default for that
                  role in the Client Care Pack. Backend enforces one
                  template per slot. */}
              <div className="space-y-2" data-testid="edit-template-pack-slot">
                <Label className="text-xs font-medium">Onboarding Pack slot</Label>
                <select
                  value={editPackSlot}
                  onChange={(e) => setEditPackSlot(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md bg-white"
                  data-testid="edit-template-pack-slot-select"
                >
                  <option value="">— Not bound to a pack slot —</option>
                  {packSlotCatalogue.map((s) => (
                    <option key={s.slot} value={s.slot}>
                      {s.label}{s.template && s.template.id !== editing?.template?.id ? ` (currently: ${s.template.name})` : ""}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-400">
                  Binding to a slot makes this template the firm-wide default for the Client Care Pack on the Authorities tab. Selecting a slot already bound to another template will move the binding.
                </p>
              </div>

              <div className="space-y-2" data-testid="edit-template-custom-tags">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Custom merge tags</Label>
                  <button
                    type="button"
                    onClick={() => setEditCustomTags([...editCustomTags, { tag: "", path: "" }])}
                    className="text-[11px] text-violet-600 hover:text-violet-700 font-medium"
                    data-testid="add-custom-tag-btn"
                  >
                    + Add tag
                  </button>
                </div>
                {editCustomTags.length === 0 ? (
                  <p className="text-[11px] text-slate-400">
                    No custom tags. Click <span className="font-medium">+ Add tag</span> to map a <code className="font-mono bg-slate-100 px-1 rounded">{"{{your_tag}}"}</code> placeholder onto any case field.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {editCustomTags.map((row, i) => (
                      <div key={i} className="flex items-center gap-1.5" data-testid={`custom-tag-row-${i}`}>
                        <Input
                          value={row.tag}
                          onChange={(e) => {
                            const copy = [...editCustomTags];
                            copy[i] = { ...copy[i], tag: e.target.value };
                            setEditCustomTags(copy);
                          }}
                          placeholder="vendor_name"
                          className="h-7 text-[11px] flex-1"
                          data-testid={`custom-tag-name-${i}`}
                        />
                        <span className="text-slate-400 text-xs">→</span>
                        <select
                          value={row.path}
                          onChange={(e) => {
                            const copy = [...editCustomTags];
                            copy[i] = { ...copy[i], path: e.target.value };
                            setEditCustomTags(copy);
                          }}
                          className="h-7 text-[11px] flex-1 border border-slate-200 rounded-md px-1.5 bg-white"
                          data-testid={`custom-tag-path-${i}`}
                        >
                          <option value="">Pick a case field…</option>
                          {Object.entries(
                            caseFields.reduce((acc, f) => {
                              const g = f.group || "Other";
                              (acc[g] = acc[g] || []).push(f);
                              return acc;
                            }, {})
                          ).map(([group, items]) => (
                            <optgroup key={group} label={group}>
                              {items.map((f) => (
                                <option key={f.path} value={f.path}>{f.label}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setEditCustomTags(editCustomTags.filter((_, j) => j !== i))}
                          className="w-6 h-6 inline-flex items-center justify-center rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          title="Remove this tag"
                          data-testid={`remove-custom-tag-${i}`}
                        >
                          <Trash weight="bold" className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <p className="text-[10px] text-slate-400">
                      Reference these as <code className="font-mono bg-slate-100 px-1 rounded">{"{{your_tag}}"}</code> inside the .docx — replaced with the chosen case field at generation time.
                    </p>
                  </div>
                )}
              </div>

              {/* Field label overrides — controls what the solicitor
                  sees in the "Fill template" form on the Case Detail
                  Documents tab. Default = humanised slug; this lets
                  the template author pick a friendlier label without
                  having to rename the underlying merge tag. */}
              <div className="space-y-2" data-testid="edit-template-field-labels">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Form field labels</Label>
                  <button
                    type="button"
                    onClick={() => setEditFieldLabels([...editFieldLabels, { tag: "", label: "" }])}
                    className="text-[11px] text-violet-600 hover:text-violet-700 font-medium"
                    data-testid="add-field-label-btn"
                  >
                    + Add label
                  </button>
                </div>
                {editFieldLabels.length === 0 ? (
                  <p className="text-[11px] text-slate-400">
                    No overrides. The fill-template form auto-humanises tags (e.g. <code className="font-mono bg-slate-100 px-1 rounded">prop_addr_l1</code> → &ldquo;Prop addr l1&rdquo;). Override one when the slug isn&apos;t friendly enough.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {editFieldLabels.map((row, i) => (
                      <div key={i} className="flex items-center gap-1.5" data-testid={`field-label-row-${i}`}>
                        <Input
                          value={row.tag}
                          onChange={(e) => {
                            const copy = [...editFieldLabels];
                            copy[i] = { ...copy[i], tag: e.target.value };
                            setEditFieldLabels(copy);
                          }}
                          placeholder="prop_addr_l1"
                          className="h-7 text-[11px] flex-1 font-mono"
                          data-testid={`field-label-tag-${i}`}
                        />
                        <span className="text-slate-400 text-xs">→</span>
                        <Input
                          value={row.label}
                          onChange={(e) => {
                            const copy = [...editFieldLabels];
                            copy[i] = { ...copy[i], label: e.target.value };
                            setEditFieldLabels(copy);
                          }}
                          placeholder="Property address line 1"
                          className="h-7 text-[11px] flex-1"
                          data-testid={`field-label-label-${i}`}
                        />
                        <select
                          value={row.kind || "text"}
                          onChange={(e) => {
                            const copy = [...editFieldLabels];
                            copy[i] = { ...copy[i], kind: e.target.value };
                            setEditFieldLabels(copy);
                          }}
                          className="h-7 text-[11px] border border-slate-200 rounded px-1.5 bg-white"
                          data-testid={`field-label-kind-${i}`}
                        >
                          <option value="text">Text</option>
                          <option value="multiline">Long text</option>
                          <option value="date">Date</option>
                          <option value="currency">£ Currency</option>
                          <option value="number">Number</option>
                          <option value="boolean">Yes / No</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => setEditFieldLabels(editFieldLabels.filter((_, j) => j !== i))}
                          className="w-6 h-6 inline-flex items-center justify-center rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          data-testid={`field-label-remove-${i}`}
                          aria-label="Remove field label"
                        >
                          <X weight="bold" className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-[11px] text-slate-500 leading-relaxed">
                To change the template's wording or merge tags, click <span className="font-medium">Replace</span> on the row instead — download the .docx, edit it in Word, then re-upload.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[12px] text-slate-600 leading-relaxed">
                Upload a new <span className="font-medium">.docx</span> to replace the contents of <span className="font-medium">{editing?.template?.name}</span>. The template id, name, and description are preserved — every solicitor's existing reference to this template keeps working.
              </p>
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5 leading-relaxed flex items-start gap-2">
                <Info weight="bold" className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
                <span>Tip: click <span className="font-medium">Download</span> on the row first to grab the current file, edit it in Word, then upload it here.</span>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">New .docx file</Label>
                <Input
                  type="file"
                  ref={replaceFileRef}
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
                  data-testid="replace-template-file"
                />
                {replaceFile && (
                  <p className="text-[11px] text-slate-500">
                    Selected: <span className="font-medium">{replaceFile.name}</span> ({(replaceFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog} disabled={savingEdit}>Cancel</Button>
            {editing?.mode === "metadata" ? (
              <Button onClick={handleSaveMetadata} disabled={savingEdit || !editName.trim()} className="text-white" style={{ background: "#100f5d" }} data-testid="edit-template-save">
                {savingEdit ? "Saving…" : "Save changes"}
              </Button>
            ) : (
              <Button onClick={handleReplaceFile} disabled={savingEdit || !replaceFile} className="text-white" style={{ background: "#100f5d" }} data-testid="replace-template-submit">
                {savingEdit ? "Replacing…" : "Replace file"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TemplatePreviewDialog
        open={!!previewTpl}
        template={previewTpl}
        onClose={() => setPreviewTpl(null)}
      />
    </div>
  );
}
